import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, ReplaySubject, map } from 'rxjs';
import {
  ActionResponse,
  AllMe,
  ChargePolicy,
  ChargingResponse,
  EnodeMe,
  EnodeVehicle,
  Intervention,
  LinkResponse,
  LocationSuggestion,
  SmartChargingPlan,
  SmartChargingStatus,
  Tariff,
  TariffConfig,
  UserLocation
} from './enode.types';
import { io } from 'socket.io-client';
import type { Socket } from 'socket.io-client';
import { KeycloakService } from 'keycloak-angular';
@Injectable({
  providedIn: 'root'
})
export class EnodeService {
  hostUrl = 'https://appgw.prod.iktest.co';
  appApiUrl = `${this.hostUrl}/api-app`;
  enodeUrl = `${this.appApiUrl}/enode`;
  vendorActionSocket: Socket;
  vehicleSocket: Socket;
  vehicleDiscoveredSocket: Socket;
  vehicleDeletedSocket: Socket;
  smartChargingSocket: Socket;
  constructor(
    private _httpClient: HttpClient,
    private _keycloakService: KeycloakService
  ) {
    this.vehicleSocket = io(this.hostUrl, {
      path: '/api-app-socketio'
    });
    this.vendorActionSocket = io(this.hostUrl, {
      path: '/api-app-socketio'
    });

    this.vehicleDiscoveredSocket = io(this.hostUrl, {
      path: '/api-app-socketio'
    });

    this.vehicleDeletedSocket = io(this.hostUrl, {
      path: '/api-app-socketio'
    });

    this.smartChargingSocket = io(this.hostUrl, {
      path: '/api-app-socketio'
    });
  }

  connectSocket() {
    this.connectVehicleSocket();
    this.connectVendorActionSocket();
    this.connectVehicleDiscoveredSocket();
    this.connectVehicleDeletedSocket();
    this.connectSmartChargingSocket();
  }

  connectVehicleSocket() {
    this._keycloakService.getToken().then((token) => {
      this.vehicleSocket.emit('whats_new', {
        token: token,
        event: 'user:vehicle:updated'
      });
    });
  }

  connectVendorActionSocket() {
    this._keycloakService.getToken().then((token) => {
      this.vendorActionSocket.emit('whats_new', {
        token: token,
        event: 'user:vendor-action:updated'
      });
    });
  }

  connectVehicleDiscoveredSocket() {
    this._keycloakService.getToken().then((token) => {
      this.vehicleDiscoveredSocket.emit('whats_new', {
        token: token,
        event: 'user:vehicle:discovered'
      });
    });
  }

  connectVehicleDeletedSocket() {
    this._keycloakService.getToken().then((token) => {
      this.vehicleDeletedSocket.emit('whats_new', {
        token: token,
        event: 'user:vehicle:deleted'
      });
    });
  }

  connectSmartChargingSocket() {
    this._keycloakService.getToken().then((token) => {
      this.smartChargingSocket.emit('whats_new', {
        token: token,
        event: 'user:vehicle:smart-charging-status-updated'
      });
    });
  }

  getVehicleStream() {
    return new Observable((observer) => {
      this.vehicleSocket.on('item', (data) => {
        observer.next(data);
      });
    });
  }

  getVendorActionStream() {
    return new Observable((observer) => {
      this.vendorActionSocket.on('item', (data) => {
        observer.next(data);
      });
    });
  }

  getVehicleDiscoveredStream() {
    return new Observable((observer) => {
      this.vehicleDiscoveredSocket.on('item', (data) => {
        observer.next(data);
      });
    });
  }

  getVehicleDeletedStream() {
    return new Observable((observer) => {
      this.vehicleDeletedSocket.on('item', (data) => {
        observer.next(data);
      });
    });
  }

  getSmartChargingStream() {
    return new Observable((observer) => {
      this.smartChargingSocket.on('item', (data) => {
        observer.next(data);
      });
    });
  }

  disconnectSocket() {
    this.vehicleSocket.disconnect();
    this.vendorActionSocket.disconnect();
    this.vehicleDiscoveredSocket.disconnect();
    this.vehicleDeletedSocket.disconnect();
    this.smartChargingSocket.disconnect();
  }

  reconnectSocket() {
    this.vehicleSocket.on('bye', () => {
      this.connectVehicleSocket();
    });

    this.vendorActionSocket.on('bye', () => {
      this.connectVendorActionSocket();
    });

    this.vehicleDiscoveredSocket.on('bye', () => {
      this.connectVehicleDiscoveredSocket();
    });

    this.vehicleDeletedSocket.on('bye', () => {
      this.connectVehicleDeletedSocket();
    });

    this.smartChargingSocket.on('bye', () => {
      this.connectSmartChargingSocket();
    });
  }

  linkVehicle(): Observable<LinkResponse> {
    let headers = new HttpHeaders({
      'Enode-Version': '2024-01-01',
      accept: '*/*'
    });
    let options = { headers: headers };
    return this._httpClient.post<LinkResponse>(
      `${this.enodeUrl}/link/vehicle`,
      {},
      options
    );
  }
  private getAllMe(): Observable<AllMe> {
    return this._httpClient.get<AllMe>(`${this.appApiUrl}/me/all`);
  }

  getMe(): Observable<EnodeMe> {
    // return this._httpClient.get<EnodeMe>(`${this.rootUrl}/me`);
    return this.getAllMe().pipe(map((res) => res.enode));
  }

  getVehicles(): Observable<EnodeVehicle[]> {
    return this._httpClient.get<EnodeVehicle[]>(`${this.enodeUrl}/vehicles`);
  }

  startCharging(vehicleId: string): Observable<ChargingResponse> {
    return this._httpClient.post<ChargingResponse>(
      `${this.enodeUrl}/charging/${vehicleId}/START`,
      {}
    );
  }

  stopCharging(vehicleId: string): Observable<ChargingResponse> {
    return this._httpClient.post<ChargingResponse>(
      `${this.enodeUrl}/charging/${vehicleId}/STOP`,
      {}
    );
  }

  getVendorAction(actionId: string): Observable<ActionResponse> {
    return this._httpClient.get<ActionResponse>(
      `${this.enodeUrl}/action/${actionId}`
    );
  }

  getLocationSuggestions(): Observable<LocationSuggestion[]> {
    return this._httpClient.get<LocationSuggestion[]>(
      `${this.enodeUrl}/locationsuggestions`
    );
  }

  setLocation(
    name: string,
    location: { lat: number; lng: number }
  ): Observable<UserLocation> {
    return this._httpClient.post<UserLocation>(`${this.enodeUrl}/location`, {
      name: name,
      latitude: location.lat,
      longitude: location.lng,
      timezoneName: 'Europe/Oslo'
    });
  }
  getLocations(): Observable<UserLocation[]> {
    return this._httpClient.get<any>(`${this.enodeUrl}/location`);
  }

  deleteLocation(locationId: string) {
    return this._httpClient.delete(`${this.enodeUrl}/location/${locationId}`);
  }

  // updateLocationName(location: UserLocation, newName: string) {
  //   return this._httpClient.put(`${this.enodeUrl}/location/${location.id}`, {
  //     name: newName
  //   });
  // }

  updateSmartChargePolicy(vehicleId: string, smartChargePolicy: ChargePolicy) {
    return this._httpClient.put<ChargePolicy>(
      `${this.enodeUrl}/vehicles/${vehicleId}/smart-charging-policy`,
      smartChargePolicy
    );
  }

  getIntervention(interventionId: string): Observable<Intervention> {
    return this._httpClient.get<Intervention>(
      `${this.enodeUrl}/intervention/${interventionId}`
    );
  }

  getSmartChargingStatus(vehicleId: string): Observable<SmartChargingStatus> {
    return this._httpClient.get<SmartChargingStatus>(
      `${this.enodeUrl}/vehicles/${vehicleId}/smart-charging-status`
    );
  }

  getSmartChargingPlans(vehicleId: string): Observable<SmartChargingPlan[]> {
    return this._httpClient.get<SmartChargingPlan[]>(
      `${this.enodeUrl}/vehicles/${vehicleId}/smart-charging-plan`
    );
  }

  getSmartChargingPlansForPeriod(
    vehicleId: string,
    period: string // YYYYMM
  ): Observable<SmartChargingPlan[]> {
    return this._httpClient.get<SmartChargingPlan[]>(
      `${this.enodeUrl}/vehicles/${vehicleId}/smart-charging-plan/${period}`
    );
  }

  getTariffConfig(locationId: string): Observable<TariffConfig> {
    return this._httpClient.get<TariffConfig>(
      `${this.enodeUrl}/location/${locationId}/tariffconfig`
    );
  }

  postTariffConfig(objectId: string, locationId: string) {
    return this._httpClient.post(
      `${this.enodeUrl}/location/${locationId}/tariffconfig`,
      {
        objectid: objectId
      }
    );
  }

  getTariff(locationId: string): Observable<Tariff[]> {
    return this._httpClient.get<Tariff[]>(
      `${this.enodeUrl}/location/${locationId}/tariff`
    );
  }

  deleteVehicle(vendor: string) {
    return this._httpClient.delete(`${this.enodeUrl}/vendor/${vendor}`);
  }
}
